import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import { Box, Button, CircularProgress, IconButton, Typography } from '@mui/material';
import { GameContext } from '../../../contexts/GameContext';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../../contexts/LanguageContext';
import SettingsButton from '../../Shared/SettingsButton';
import MenuButton from '../../Shared/MenuButton';
import { PlaylistType } from '../../../utils/PlaylistsTypes';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

const playlistCards: Record<string, { gradient: string; emoji: string }> = {
    [PlaylistType.BestHitFrance]: {
        gradient: 'linear-gradient(135deg, #e85d6c, #e8956b)',
        emoji: '⭐',
    },
    [PlaylistType.France]: {
        gradient: 'linear-gradient(135deg, #5bc8e0, #a87dc8)',
        emoji: '🥖',
    },
    [PlaylistType.GuiltyPleasures]: {
        gradient: 'linear-gradient(135deg, #a87dc8, #e85d9a)',
        emoji: '🙈',
    },
    [PlaylistType.RockDeutschland]: {
        gradient: 'linear-gradient(135deg, #e85d6c, #a83248)',
        emoji: '🎸',
    },
    [PlaylistType.BestHitUsa]: {
        gradient: 'linear-gradient(135deg, #e85d6c, #e8956b)',
        emoji: '⭐',
    },
};

const playlistsByCountry = [
    {
        countryKey: 'country_france',
        flag: 'fr',
        playlists: [PlaylistType.BestHitFrance, PlaylistType.France, PlaylistType.GuiltyPleasures],
    },
    {
        countryKey: 'country_usa',
        flag: 'us',
        playlists: [PlaylistType.BestHitUsa],
    },
    {
        countryKey: 'country_deutschland',
        flag: 'de',
        playlists: [PlaylistType.RockDeutschland],
    },
];

const WaitingLobby = () => {
    const gameContext = useContext(GameContext);
    const navigate = useNavigate();
    const { language, setLanguage, t } = useTranslation();

    const [copied, setCopied] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleBackToHome = async () => {
        await gameContext.getClient().LeaveGame();
        navigate('/');
    };

    useEffect(() => {
        if (gameContext.isCreator && !gameContext.selectedPlaylist) {
            gameContext.getClient().SelectPlaylist(PlaylistType.BestHitFrance);
        }
    }, [gameContext.isCreator]);

    const handleStartGame = async () => {
        setLoading(true);
        await gameContext.getClient().StartGame();
    };

    const handleInvite = async () => {
        const url = `${window.location.origin}?join=${gameContext.gameId}`;
        await navigator.clipboard.writeText(url);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleCardsToWinChange = async (delta: number) => {
        const newValue = gameContext.cardsToWin + delta;
        if (newValue < 3 || newValue > 20) return;
        await gameContext.getClient().SetCardsToWin(newValue);
    };

    const handleSelectPlaylist = async (playlistName: string) => {
        if (!gameContext.isCreator) {
            return;
        }

        await gameContext.getClient().SelectPlaylist(playlistName);
    };

    return (
        <>
            {!gameContext.gameStarted && (
                <>
                    <Box sx={{ position: 'fixed', top: 16, left: 16, zIndex: 1400 }}>
                        <MenuButton
                            text={t('home')}
                            buttonWidth="auto"
                            textSize="h5"
                            onClick={handleBackToHome}
                            background="url(/images/buttons/menuButton1.png)"
                            hoverBackground="url(/images/buttons/menuButton1_hover.png)"
                            dynamicCursor={false}
                            staticCursorImage=""
                            disabled={false}
                        />
                    </Box>
                    <Box sx={{ position: 'fixed', top: 16, right: 16, zIndex: 1400, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <SettingsButton />
                        <Button
                            variant="outlined"
                            onClick={() => setLanguage(language === 'fr' ? 'en' : 'fr')}
                            sx={{
                                minWidth: 'auto',
                                padding: '4px 12px',
                                color: '#c191ff',
                                borderColor: 'rgba(193, 145, 255, 0.4)',
                                textTransform: 'none',
                                fontSize: '0.85rem',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    borderColor: '#c191ff',
                                    backgroundColor: 'rgba(193, 145, 255, 0.1)',
                                },
                            }}
                        >
                            {language === 'fr' ? (
                                <><img src="https://flagcdn.com/w20/gb.png" alt="EN" style={{ width: 16, height: 12, marginRight: 4, verticalAlign: 'middle' }} /> EN</>
                            ) : (
                                <><img src="https://flagcdn.com/w20/fr.png" alt="FR" style={{ width: 16, height: 12, marginRight: 4, verticalAlign: 'middle' }} /> FR</>
                            )}
                        </Button>
                    </Box>
                </>
            )}
            <Dialog
                open={!gameContext.gameStarted}
                disableEscapeKeyDown
                slotProps={{
                    backdrop: {
                        sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
                    }
                }}
                PaperProps={{
                    sx: {
                        backgroundColor: '#1a2340',
                        color: 'white',
                        width: 460,
                        maxWidth: '95vw',
                        borderRadius: 3,
                        padding: 2,
                        maxHeight: '90vh',
                    }
                }}
            >
                <DialogContent sx={{ textAlign: 'center', px: 2, py: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="h4" sx={{ mb: 2, fontWeight: 'bold', textShadow: '0 0 20px rgba(193, 145, 255, 0.4)' }}>
                        {t('newGame')}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1.5, mb: 2.5 }}>
                        <Typography variant="body1" sx={{ opacity: 0.7 }}>
                            {t('code')} : {gameContext.gameId}
                        </Typography>
                        <Button
                            size="small"
                            onClick={handleInvite}
                            disabled={loading}
                            sx={{
                                textTransform: 'none',
                                fontWeight: 'bold',
                                fontSize: '0.8rem',
                                borderRadius: 1,
                                px: 2,
                                py: 0.5,
                                minWidth: 0,
                                color: 'black',
                                backgroundImage: 'url(/images/buttons/menuButton1.png)',
                                backgroundSize: '100% 100%',
                                backgroundPosition: 'center',
                                backgroundRepeat: 'no-repeat',
                                '&:hover': {
                                    backgroundImage: 'url(/images/buttons/menuButton1_hover.png)'
                                }
                            }}
                        >
                            {copied ? t('linkCopied') : t('invite')}
                        </Button>
                    </Box>

                    {/* Playlist selection */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                            mb: 2.5,
                            maxHeight: 340,
                            overflowY: 'auto',
                            px: 1,
                            textAlign: 'left',
                            '&::-webkit-scrollbar': { width: 4 },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(193, 145, 255, 0.3)',
                                borderRadius: 2,
                            },
                        }}
                    >
                        {playlistsByCountry.map((section) => (
                            <Box key={section.countryKey}>
                                {/* Country header */}
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.2 }}>
                                    <img
                                        src={`https://flagcdn.com/w40/${section.flag}.png`}
                                        alt={t(section.countryKey)}
                                        style={{ width: 22, height: 15, borderRadius: 2 }}
                                    />
                                    <Typography sx={{
                                        fontSize: '0.85rem',
                                        fontWeight: 800,
                                        color: 'rgba(255,255,255,0.6)',
                                        textTransform: 'uppercase',
                                        letterSpacing: 1.5,
                                    }}>
                                        {t(section.countryKey)}
                                    </Typography>
                                </Box>

                                {/* Playlist cards row */}
                                <Box sx={{
                                    display: 'flex',
                                    gap: 1.2,
                                    flexWrap: 'wrap',
                                }}>
                                    {section.playlists.map((key) => {
                                        const card = playlistCards[key];
                                        const isSelected = gameContext.selectedPlaylist === key;
                                        return (
                                            <Box
                                                key={key}
                                                onClick={() => !loading && handleSelectPlaylist(key)}
                                                sx={{
                                                    position: 'relative',
                                                    width: 110,
                                                    flexShrink: 0,
                                                    borderRadius: 3,
                                                    overflow: 'hidden',
                                                    cursor: gameContext.isCreator && !loading ? 'pointer' : 'default',
                                                    opacity: loading ? 0.5 : 1,
                                                    transition: 'all 0.25s ease',
                                                    border: isSelected ? '3px solid #fff' : '3px solid transparent',
                                                    transform: isSelected ? 'scale(1.03)' : 'scale(1)',
                                                    ...(gameContext.isCreator && {
                                                        '&:hover': {
                                                            transform: 'scale(1.05)',
                                                        },
                                                    }),
                                                }}
                                            >
                                                {/* Gradient background */}
                                                <Box sx={{
                                                    background: card.gradient,
                                                    height: 85,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: 0.5,
                                                    p: 1.5,
                                                }}>
                                                    <Typography sx={{
                                                        fontSize: '2rem',
                                                        lineHeight: 1,
                                                    }}>
                                                        {card.emoji}
                                                    </Typography>
                                                    <Typography sx={{
                                                        fontSize: '0.8rem',
                                                        fontWeight: 800,
                                                        color: '#fff',
                                                        textAlign: 'center',
                                                        lineHeight: 1.2,
                                                        textTransform: 'uppercase',
                                                        letterSpacing: 0.5,
                                                    }}>
                                                        {t(key)}
                                                    </Typography>
                                                </Box>

                                                {/* Selected checkmark */}
                                                {isSelected && (
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        top: 6,
                                                        right: 6,
                                                        width: 22,
                                                        height: 22,
                                                        borderRadius: '50%',
                                                        backgroundColor: '#fff',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}>
                                                        <Typography sx={{ fontSize: '0.75rem', color: '#1a2340', fontWeight: 'bold', lineHeight: 1 }}>
                                                            ✓
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        ))}
                    </Box>

                    {/* Cards to win selector */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 2,
                        mb: 2.5,
                        py: 1.2,
                        px: 2,
                        borderRadius: 2,
                        backgroundColor: 'rgba(193, 145, 255, 0.08)',
                        border: '1px solid rgba(193, 145, 255, 0.15)',
                    }}>
                        <Typography sx={{ fontSize: '0.9rem', fontWeight: 600, color: 'rgba(255,255,255,0.8)' }}>
                            {t('cardsToWin')}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton
                                size="small"
                                onClick={() => handleCardsToWinChange(-1)}
                                disabled={!gameContext.isCreator || loading || gameContext.cardsToWin <= 3}
                                sx={{
                                    color: '#c191ff',
                                    border: '1px solid rgba(193, 145, 255, 0.3)',
                                    width: 30,
                                    height: 30,
                                    '&.Mui-disabled': { color: 'rgba(193, 145, 255, 0.2)', borderColor: 'rgba(193, 145, 255, 0.1)' },
                                }}
                            >
                                <RemoveIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                            <Typography sx={{
                                fontSize: '1.3rem',
                                fontWeight: 'bold',
                                color: '#c191ff',
                                minWidth: 30,
                                textAlign: 'center',
                            }}>
                                {gameContext.cardsToWin}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => handleCardsToWinChange(1)}
                                disabled={!gameContext.isCreator || loading || gameContext.cardsToWin >= 20}
                                sx={{
                                    color: '#c191ff',
                                    border: '1px solid rgba(193, 145, 255, 0.3)',
                                    width: 30,
                                    height: 30,
                                    '&.Mui-disabled': { color: 'rgba(193, 145, 255, 0.2)', borderColor: 'rgba(193, 145, 255, 0.1)' },
                                }}
                            >
                                <AddIcon sx={{ fontSize: '1rem' }} />
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Connected players */}
                    <Typography variant="body2" sx={{ mb: 1, color: '#c191ff', fontWeight: 'bold', textShadow: '0 0 12px rgba(193, 145, 255, 0.3)' }}>
                        {t('connectedPlayers')}
                    </Typography>

                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: gameContext.players.length > 4 ? '1fr 1fr' : '1fr',
                            gap: 0.8,
                            overflowY: 'auto',
                            maxHeight: 160,
                            mb: 2,
                            px: 1,
                            '&::-webkit-scrollbar': {
                                width: 4
                            },
                            '&::-webkit-scrollbar-thumb': {
                                backgroundColor: 'rgba(193, 145, 255, 0.3)',
                                borderRadius: 2
                            }
                        }}
                    >
                        {gameContext.players.map((player) => (
                            <Box
                                key={player.playerId}
                                sx={{
                                    py: 0.6,
                                    px: 1.5,
                                    borderRadius: 1.5,
                                    backgroundColor: 'rgba(255,255,255,0.06)',
                                    textAlign: 'center',
                                    fontSize: '0.95rem'
                                }}
                            >
                                {player.playerName}
                            </Box>
                        ))}
                    </Box>
                </DialogContent>

                <Button
                    onClick={handleStartGame}
                    disabled={!gameContext.isCreator || loading}
                    sx={{
                        fontSize: '1.2rem',
                        fontWeight: 'bold',
                        padding: '10px 40px',
                        borderRadius: 1,
                        mx: 2,
                        mb: 2,
                        color: 'black',
                        backgroundImage: gameContext.isCreator ? 'url(/images/buttons/menuButton1.png)' : 'url(/images/buttons/menuButton1_disabled.png)',
                        backgroundSize: '100% 100%',
                        backgroundPosition: 'center',
                        backgroundRepeat: 'no-repeat',
                        '&:hover': {
                            backgroundImage: 'url(/images/buttons/menuButton1_hover.png)'
                        },
                        '&.Mui-disabled': {
                            color: 'rgba(0,0,0,0.4)',
                            backgroundImage: 'url(/images/buttons/menuButton1_disabled.png)',
                            backgroundSize: '100% 100%',
                            backgroundPosition: 'center',
                            backgroundRepeat: 'no-repeat'
                        }
                    }}
                >
                    {loading ? <CircularProgress size={24} sx={{ color: 'black' }} /> : gameContext.isCreator ? "Let's gooo" : t('waitingOrganizer')}
                </Button>
            </Dialog>
        </>
    );
};

export default WaitingLobby;
